'use client';

import { createClient } from '@/utils/supabase/client';
import { User } from '@supabase/supabase-js';
import { usePathname } from 'next/navigation';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const NAVIGATION_REFRESH_DEBOUNCE_MS = 120;

interface BrowserAuthUserState {
  user: User | null;
  isLoading: boolean;
}

export function useBrowserAuthUser(): BrowserAuthUserState {
  const pathname = usePathname();
  const supabase = useMemo(() => createClient(), []);
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const isMountedRef = useRef(false);
  const hasInitialLoadRef = useRef(false);
  const refreshRequestIdRef = useRef(0);
  const navigationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearNavigationTimeout = useCallback(() => {
    if (!navigationTimeoutRef.current) {
      return;
    }

    clearTimeout(navigationTimeoutRef.current);
    navigationTimeoutRef.current = null;
  }, []);

  const refreshAuthoritativeUser = useCallback(async () => {
    const requestId = ++refreshRequestIdRef.current;

    const {
      data: { user: currentUser },
    } = await supabase.auth.getUser();

    if (!isMountedRef.current) {
      return;
    }

    // Ignore stale/out-of-order responses so older requests cannot overwrite fresh state.
    if (requestId !== refreshRequestIdRef.current) {
      return;
    }

    setUser(currentUser ?? null);

    // add a artificial 5sec delay
    //await new Promise((resolve) => setTimeout(resolve, 5000));

    setIsLoading(false);
  }, [supabase]);

  useEffect(() => {
    isMountedRef.current = true;

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(() => {
      if (!isMountedRef.current) {
        return;
      }

      clearNavigationTimeout();
      setIsLoading(true);
      void refreshAuthoritativeUser();
    });

    return () => {
      isMountedRef.current = false;
      clearNavigationTimeout();
      subscription.unsubscribe();
    };
  }, [supabase, refreshAuthoritativeUser, clearNavigationTimeout]);

  useEffect(() => {
    clearNavigationTimeout();

    if (!hasInitialLoadRef.current) {
      hasInitialLoadRef.current = true;
      setIsLoading(true);
      void refreshAuthoritativeUser();
      return;
    }

    setIsLoading(true);
    navigationTimeoutRef.current = setTimeout(() => {
      void refreshAuthoritativeUser();
    }, NAVIGATION_REFRESH_DEBOUNCE_MS);

    return () => {
      clearNavigationTimeout();
    };
  }, [pathname, refreshAuthoritativeUser, clearNavigationTimeout]);

  return {
    user,
    isLoading,
  };
}
